import type { ComponentPropsWithoutRef } from "react"
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react"
import clsx from "clsx"
import { IconLoaderOutline18 as Loader } from "nucleo-ui-outline-18"
import { createPortal } from "react-dom"

import { Avatar, Popover } from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import type {
	SuggestionKeyDownProps,
	SuggestionProps,
} from "@tiptap/suggestion"
import { api } from "@/lib/api"
import { matchMentions } from "./mention-matches"

type MentionListProps = SuggestionProps

interface MentionListActions {
	onKeyDown: (props: SuggestionKeyDownProps) => void
}

const MentionList = forwardRef<MentionListActions, MentionListProps>(
	({ command, query, clientRect }, ref) => {
		const userQuery = api.workspace.members.useQuery(undefined)

		const memberships = useMemo(
			() => matchMentions(userQuery.data ?? [], query),
			[query, userQuery.data],
		)

		const coords = useMemo(() => {
			if (clientRect) {
				const coords = clientRect()
				if (coords) {
					return { top: coords.top, left: coords.left }
				}
			}
		}, [clientRect])

		const handleCommand = useCallback(
			(index: number) => {
				const selectedPerson = memberships[index]?.user
				if (selectedPerson) {
					command({ id: selectedPerson.id, label: selectedPerson.name })
				}
			},
			[command, memberships],
		)

		const [hoverIndex, setHoverIndex] = useState(0)

		// Really basic keyboard navigation
		const upHandler = () =>
			setHoverIndex((hoverIndex + memberships.length - 1) % memberships.length)
		const downHandler = () =>
			setHoverIndex((hoverIndex + 1) % memberships.length)
		const enterHandler = () => handleCommand(hoverIndex)

		useEffect(() => setHoverIndex(0), [memberships])

		useImperativeHandle(ref, () => ({
			onKeyDown: ({ event }: { event: KeyboardEvent }) => {
				if (event.key === "ArrowUp") {
					upHandler()
					return true
				}

				if (event.key === "ArrowDown") {
					downHandler()
					return true
				}

				if (event.key === "Enter") {
					enterHandler()
					return true
				}

				if (event.key === "Tab") {
					enterHandler()
					return true
				}

				return false
			},
		}))

		if (!coords || typeof document === "undefined") {
			return null
		}

		// We need to do a bit of custom positioning to align the suggestions with the editor
		return (
			<>
				{createPortal(
					<div
						className="absolute"
						style={{ top: coords.top, left: coords.left }}
					>
						<Popover
							open
							defaultOpen
							modal={false}
							anchor={<div className="h-4" />}
							className="max-h-56 overflow-auto p-2"
							onOpenAutoFocus={(e) => {
								// don't move focus into the popover as this would remove focus from the editor
								e.preventDefault()
							}}
							content={
								userQuery.isLoading ? (
									<Loader />
								) : memberships.length === 0 ? (
									<div className="text-sm">No users found</div>
								) : (
									memberships.map((ms, index) => (
										<MentionItem
											key={ms.id}
											isActive={index === hoverIndex}
											onMouseEnter={() => setHoverIndex(index)}
											onClick={() => handleCommand(index)}
											membership={ms}
										/>
									))
								)
							}
						/>
					</div>,
					document.body,
				)}
			</>
		)
	},
)

interface MentionItemProps extends ComponentPropsWithoutRef<"div"> {
	className?: string
	isActive: boolean
	membership: RouterOutputs["workspace"]["members"][number]
}

function MentionItem({
	isActive,
	className,
	membership,
	...props
}: MentionItemProps) {
	const ref = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		if (isActive) {
			ref.current?.scrollIntoView({ block: "nearest" })
		}
	}, [isActive])

	return (
		<div className="relative w-full truncate rounded-md" {...props} ref={ref}>
			<div
				className={clsx(
					"relative z-1 flex select-none items-center gap-2 truncate p-2 text-sm",
					isActive
						? "bg-gray-100 dark:bg-gray-800"
						: "hover:bg-gray-100 dark:hover:bg-gray-800",
					className,
				)}
			>
				<Avatar
					size="sm"
					name={membership.user.name ?? membership.user.email}
					image={membership.user.imageUrl}
					roundness="full"
				/>
				<div className="flex flex-col -space-y-0.5 truncate">
					<div className="flex-1 truncate font-medium">
						{membership.user.name}
					</div>
					<div className="flex-1 truncate text-xs opacity-80">
						{membership.user.email}
					</div>
				</div>
			</div>
		</div>
	)
}

export default MentionList
